import { useState, useCallback, useEffect, useRef } from 'react';
import { videosService } from '../../../shared/services/videosService';
import { videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';
import { CaptureItem, Video } from '../types';
import { formatFileSize } from '../utils';

interface UseCaptureDataReturn {
  folders: CaptureItem[];
  files: CaptureItem[];
  folderTree: CaptureItem;
  isLoading: boolean;
  videosByFolder: Record<string, Video>;
  loadFolderContents: (folderPath?: string) => Promise<void>;
  loadFolderTree: () => Promise<void>;
}

/**
 * Custom hook for managing capture app data (folders, files, folder tree)
 */
export const useCaptureData = (orgId: string): UseCaptureDataReturn => {
  const [folders, setFolders] = useState<CaptureItem[]>([]);
  const [files, setFiles] = useState<CaptureItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [folderTree, setFolderTree] = useState<CaptureItem>({
    id: 'root',
    name: 'All Files',
    type: 'folder',
    children: [],
  });
  const [videosByFolder, setVideosByFolder] = useState<Record<string, Video>>({});
  const [assetContents, setAssetContents] = useState<Record<string, CaptureItem[]>>({});

  const videosByFolderRef = useRef<Record<string, Video>>({});
  const assetContentsRef = useRef<Record<string, CaptureItem[]>>({});

  useEffect(() => {
    videosByFolderRef.current = videosByFolder;
  }, [videosByFolder]);

  useEffect(() => {
    assetContentsRef.current = assetContents;
  }, [assetContents]);

  const normalizeFolder = useCallback(
    (storagePath: string): string => {
      const relative = storagePath.replace(`org_${orgId}/`, '');
      const [folderName] = relative.split('/');
      return folderName || '';
    },
    [orgId]
  );

  // Load folder contents
  const createAssetFolderItem = useCallback(
    (videoFolder: string, assetName: 'thumbnails' | 'clips', videoId?: string): CaptureItem => ({
      id: `folder-${videoFolder}-${assetName}`,
      name: assetName,
      type: 'folder',
      path: `${videoFolder}/${assetName}`,
      isAsset: true,
      assetKind: 'folder',
      parentVideoId: videoId,
    }),
    []
  );

  const buildMasterItem = useCallback(
    (video: Video): CaptureItem => ({
      id: `master-${video.id}`,
      name: 'master.mp4',
      type: 'file',
      size: video.size_bytes ? formatFileSize(video.size_bytes) : undefined,
      updatedAt: video.updated_at,
      status:
        video.status === 'ready'
          ? 'ready'
          : video.status === 'error'
          ? 'error'
          : video.status === 'pending_upload'
          ? 'pending_upload'
          : video.status === 'uploaded'
          ? 'uploaded'
          : 'processing',
      videoId: video.id,
      storagePath: video.storage_path,
      description: video.description || null,
      video,
      isAsset: true,
      assetKind: 'master',
      parentVideoId: video.id,
    }),
    []
  );

  const fetchThumbnailItems = useCallback(
    async (video: Video, videoFolder: string): Promise<CaptureItem[]> => {
      if (video.status !== 'ready') {
        return [];
      }
      const result = await videoProcessingJobsService.getJobsByVideoId(video.id);
      if (!result.success || !result.data) return [];

      const thumbnailJobs = result.data.filter(
        (job) => job.job_type === 'thumbnail' && job.status === 'succeeded' && job.is_active
      );

      const assetMap = new Map<string, CaptureItem>();

      thumbnailJobs.forEach((job) => {
        const generatedAssets = Array.isArray(job.payload?.generatedAssets)
          ? job.payload.generatedAssets
          : [];

        if (generatedAssets.length > 0) {
          generatedAssets.forEach((asset, index) => {
            if (!asset || !asset.storagePath) return;
            const filename = asset.storagePath.split('/').pop() || `thumbnail-${index}.webp`;
            const item: CaptureItem = {
              id: `thumb-${video.id}-${filename}`,
              name: filename,
              type: 'file',
              status: 'ready',
              updatedAt: asset.createdAt || video.updated_at,
              storagePath: asset.storagePath,
              isAsset: true,
              assetKind: 'thumbnail',
              parentVideoId: video.id,
              video,
              size: asset.sizeBytes ? formatFileSize(asset.sizeBytes) : undefined,
              assetMetadata: {
                storagePath: asset.storagePath,
                publicUrl: asset.publicUrl ?? null,
                contentType: asset.contentType ?? 'image/webp',
                sizeBytes: asset.sizeBytes ?? null,
                width: asset.width ?? null,
                height: asset.height ?? null,
                timestampMs: asset.timestampMs ?? null,
                createdAt: asset.createdAt ?? null,
              },
            };
            assetMap.set(asset.storagePath, item);
          });
          return;
        }

        const timestamps = Array.isArray(job.payload?.timestamps)
          ? job.payload.timestamps
          : [];
        timestamps.forEach((ts) => {
          const value =
            typeof ts === 'number' ? ts : typeof ts === 'string' ? parseFloat(ts) : NaN;
          if (!Number.isFinite(value)) return;
          const filename = `${Math.round(value * 1000)}.jpg`;
          const storagePath = `org_${orgId}/${videoFolder}/thumbnails/${filename}`;
          assetMap.set(storagePath, {
            id: `thumb-${video.id}-${filename}`,
            name: filename,
            type: 'file',
            status: 'ready',
            updatedAt: video.updated_at,
            storagePath,
            isAsset: true,
            assetKind: 'thumbnail',
            parentVideoId: video.id,
            video,
          });
        });
      });

      return Array.from(assetMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    [orgId]
  );

  const loadAssetFolder = useCallback(
    async (folderPath: string) => {
      const cached = assetContentsRef.current[folderPath];
      if (cached) {
        setFolders([]);
        setFiles(cached);
        return;
      }

      const [videoFolder, assetName] = folderPath.split('/');
      if (!videoFolder || !assetName) {
        setFolders([]);
        setFiles([]);
        return;
      }

      const video = videosByFolderRef.current[videoFolder];
      if (!video) {
        setFolders([]);
        setFiles([]);
        return;
      }
      if (video.status !== 'ready') {
        setFolders([]);
        setFiles([]);
        return;
      }

      if (assetName === 'thumbnails') {
        const thumbnails = await fetchThumbnailItems(video, videoFolder);
        setAssetContents((prev) => ({
          ...prev,
          [folderPath]: thumbnails,
        }));
        setFolders([]);
        setFiles(thumbnails);
        return;
      }

      if (assetName === 'clips') {
        setAssetContents((prev) => ({
          ...prev,
          [folderPath]: prev[folderPath] || [],
        }));
        setFolders([]);
        setFiles(assetContentsRef.current[folderPath] || []);
        return;
      }

      setFolders([]);
      setFiles([]);
    },
    [fetchThumbnailItems]
  );

  const loadFolderContents = useCallback(
    async (folderPath: string = '') => {
      if (!orgId) return;

      if (folderPath.includes('/') && folderPath.trim().length > 0) {
        setIsLoading(true);
        try {
          await loadAssetFolder(folderPath);
        } catch (error) {
          console.error('Error loading asset folder:', error);
          setFolders([]);
          setFiles([]);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      try {
        const videosResult = await videosService.getVideosByOrg(orgId);
        const allVideos = videosResult.success && videosResult.data ? videosResult.data : [];

        const folderVideoMap: Record<string, Video> = {};
        allVideos.forEach((video) => {
          const folderName = normalizeFolder(video.storage_path);
          if (folderName) {
            folderVideoMap[folderName] = video;
          }
        });
        setVideosByFolder(folderVideoMap);

        if (!folderPath) {
          const folderItems: CaptureItem[] = Object.entries(folderVideoMap)
            .map(([key, video]) => ({
              id: `folder-${key}`,
              name: video.file_name || key,
              type: 'folder' as const,
              path: key,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setFolders(folderItems);
          setFiles([]);
          return;
        }

        const video = folderVideoMap[folderPath];
        if (!video) {
          setFolders([]);
          setFiles([]);
          return;
        }

        setFiles([buildMasterItem(video)]);

        if (video.status !== 'ready') {
          setFolders([]);
          return;
        }

        const derivedFolders: CaptureItem[] = [
          createAssetFolderItem(folderPath, 'thumbnails', video.id),
        ];

        const clipsFolderKey = `${folderPath}/clips`;
        if ((assetContentsRef.current[clipsFolderKey] || []).length > 0) {
          derivedFolders.push(createAssetFolderItem(folderPath, 'clips', video.id));
        }

        setFolders(derivedFolders);

        const thumbnailsKey = `${folderPath}/thumbnails`;
        if (!assetContentsRef.current[thumbnailsKey]) {
          fetchThumbnailItems(video, folderPath)
            .then((thumbnails) => {
              setAssetContents((prev) => ({
                ...prev,
                [thumbnailsKey]: thumbnails,
              }));
            })
            .catch((error) => console.error('Error fetching thumbnails:', error));
        }

        const clipsKey = `${folderPath}/clips`;
        if (!assetContentsRef.current[clipsKey]) {
          setAssetContents((prev) => ({
            ...prev,
            [clipsKey]: [],
          }));
        }
      } catch (error) {
        console.error('Error loading folder contents:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      orgId,
      normalizeFolder,
      createAssetFolderItem,
      buildMasterItem,
      fetchThumbnailItems,
      loadAssetFolder,
    ]
  );

  // Load folder tree structure (recursively loads subfolders)
  const loadFolderTree = useCallback(async () => {
    if (!orgId) return;
    const videosResult = await videosService.getVideosByOrg(orgId);
    const allVideos = videosResult.success && videosResult.data ? videosResult.data : [];

    const rootChildren = allVideos
      .reduce<CaptureItem[]>((acc, video) => {
        const folderName = normalizeFolder(video.storage_path);
        if (!folderName) {
          return acc;
        }
        acc.push({
          id: `folder-${folderName}`,
          name: video.file_name || folderName,
          type: 'folder',
          path: folderName,
          children: [],
        });
        return acc;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));

    setFolderTree({
      id: 'root',
      name: 'All Files',
      type: 'folder',
      children: rootChildren,
    });
  }, [orgId, normalizeFolder]);

  // Initial load
  useEffect(() => {
    if (orgId) {
      loadFolderTree();
      loadFolderContents('');
    }
  }, [orgId, loadFolderTree, loadFolderContents]);

  return {
    folders,
    files,
    folderTree,
    isLoading,
    videosByFolder,
    loadFolderContents,
    loadFolderTree,
  };
};

